import React, { useMemo } from 'react';
import { ReactComponent as WifiOff } from '../../../images/wifi-off.svg';
import './CommStatus.scss';

export enum CommStatus {
  Ok = 1,
  Warning = 2,
  Error = 3,
  Unknown = 4,
}

interface CommStatusProps {
  commStatus: string | number;
  commsPercent: number;
  className?: string;
}

// Signal strength thresholds
const SIGNAL_THRESHOLDS = {
  EXCELLENT: 95,
  GOOD: 90,
  FAIR: 80,
  MIN_BARS: 1,
  MAX_BARS: 3,
} as const;

const SVG_CONFIG = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  strokeWidth: '2.33',
  activeOpacity: 1,
  inactiveOpacity: 0.3,
} as const;

// WiFi signal paths for different strength levels
const WIFI_PATHS = [
  {
    id: 'center-dot',
    d: 'M12 19.5H12.01',
    minBars: 0,
  },
  {
    id: 'inner-arc',
    d: 'M8.25537 15.8116C9.28172 14.9908 10.5835 14.5 11.9999 14.5C13.3952 14.5 14.6792 14.9763 15.6983 15.7751',
    minBars: 1,
  },
  {
    id: 'middle-arc',
    d: 'M4.73193 12.243C6.67006 10.5357 9.21407 9.5 12 9.5C14.7859 9.5 17.3299 10.5357 19.268 12.243',
    minBars: 2,
  },
  {
    id: 'outer-arc',
    d: 'M1.19336 8.70076C4.04023 6.09199 7.83414 4.5 11.9999 4.5C16.1656 4.5 19.9595 6.09199 22.8064 8.70076',
    minBars: 3,
  },
] as const;

//Determines signal strength bars based on communication percentage
const getSignalBars = (commsPercent: number): number => {
  if (commsPercent >= SIGNAL_THRESHOLDS.EXCELLENT) return 3;
  if (commsPercent >= SIGNAL_THRESHOLDS.GOOD) return 2;
  if (commsPercent >= SIGNAL_THRESHOLDS.FAIR) return 1;
  return 0;
};

const isCommStatusDisabled = (status: number): boolean => {
  return status === CommStatus.Unknown || status === CommStatus.Error || status === CommStatus.Warning;
};

const CommStatusIcon: React.FC<CommStatusProps> = React.memo(({ commStatus, commsPercent, className = '' }) => {
  const status = useMemo(() => Number(commStatus), [commStatus]);
  const bars = useMemo(() => getSignalBars(commsPercent), [commsPercent]);
  const isDisabled = useMemo(() => isCommStatusDisabled(status), [status]);

  if (isDisabled) {
    return (
      <WifiOff className={`comm-status-icon disabled ${className}`} aria-label='Communication disabled' role='img' />
    );
  }

  return (
    <div
      className={`comm-status-icon wifi ${className}`}
      aria-label={`Communication signal strength: ${bars} of ${SIGNAL_THRESHOLDS.MAX_BARS} bars`}
      role='img'
    >
      <svg
        width={SVG_CONFIG.width}
        height={SVG_CONFIG.height}
        viewBox={SVG_CONFIG.viewBox}
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        {WIFI_PATHS.map(({ id, d, minBars }) => (
          <path
            key={id}
            d={d}
            stroke='currentColor'
            strokeWidth={SVG_CONFIG.strokeWidth}
            strokeLinecap='round'
            strokeLinejoin='round'
            opacity={bars >= minBars ? SVG_CONFIG.activeOpacity : SVG_CONFIG.inactiveOpacity}
          />
        ))}
      </svg>
    </div>
  );
});

CommStatusIcon.displayName = 'CommStatusIcon';

export default CommStatusIcon;
